/**
 * MASSEUTSENDING (F34, runde 4. oktober — lukker sak 66): beskjed, vaktplan og vaktinfo på e-post til
 * frivillige og personer i en festival eller et spillested. De med innlogging i organisasjonen får
 * meldingen i Innboks i tillegg.
 *
 * Mottakerne velges fra lista: alle, en avdeling (festival), en post (spillested), en vakt, eller de
 * som er krysset av. Serveren slår dem opp i organisasjonen selv — klienten sender aldri adresser.
 *
 * Cella er `broadcast:create` (sende) og `broadcast:list` (se hva som er sendt), ett valg i
 * Rettigheter › Mannskap og vakter. E-posten går gjennom utsendingslaget (køen, feieren og Brevo);
 * når dagsgrensen hos Brevo er nådd, blir meldingene liggende og sendes når grensen er tilbake —
 * statusen `held` er det den som sendte ser.
 */
import { z } from 'zod';

import { DEFAULT_PAGE_SIZE, MAX_CURSOR_LENGTH, MAX_PAGE_SIZE, page } from '../http/pagination.js';
import { wireInstant } from './wire-time.js';

/** Så mange mottakere én utsending kan ha. Gratisplanen hos Brevo er 300 e-poster om dagen. */
export const MAX_BROADCAST_RECIPIENTS = 1000;
export const MAX_BROADCAST_SUBJECT_LENGTH = 200;
export const MAX_BROADCAST_BODY_LENGTH = 10_000;
/** Vedlegg fra Dokumenter (persontypene §4.1): så mange filer, og så mye til sammen. */
export const MAX_BROADCAST_ATTACHMENTS = 5;
export const MAX_BROADCAST_ATTACHMENT_BYTES = 10 * 1024 * 1024;

/**
 * KATEGORIENE I «SEND BESKJED» (persontypene §4.1). Deltakelsens kategorier, pluss lenkepersonene
 * (`recipient_links` i årgangen) og de eksterne (innlogging og en levende deling i årgangen). En
 * person kan ha flere; hen er med når én av dem er valgt.
 */
export const BROADCAST_RECIPIENT_CATEGORIES = [
  'volunteer',
  'supplier',
  'staff',
  'artist',
  'guest',
  'press',
  'link',
  'external',
] as const;
export const broadcastRecipientCategorySchema = z.enum(BROADCAST_RECIPIENT_CATEGORIES);
export type BroadcastRecipientCategory = z.infer<typeof broadcastRecipientCategorySchema>;

/** Godkjent (står i årgangen), eller venter (påmelding som frivillig eller leverandør som ikke er behandlet). */
export const BROADCAST_RECIPIENT_STATUSES = ['approved', 'pending'] as const;
export const broadcastRecipientStatusSchema = z.enum(BROADCAST_RECIPIENT_STATUSES);
export type BroadcastRecipientStatus = z.infer<typeof broadcastRecipientStatusSchema>;

/**
 * HVOR «PERSONLIG LENKE» GÅR, PER MOTTAKER (§4.1): den personlige siden (frivillig, leverandørens
 * kontaktperson, lenkeperson), Mine vakter (medarbeider), Mine konserter (artist eller manager med
 * innlogging) eller Delte produksjoner (ekstern med innlogging). `null`: ingen lenke å gi.
 */
export const BROADCAST_LINK_TARGETS = [
  'personal_page',
  'my_shifts',
  'my_concerts',
  'shared_productions',
] as const;
export const broadcastLinkTargetSchema = z.enum(BROADCAST_LINK_TARGETS);
export type BroadcastLinkTarget = z.infer<typeof broadcastLinkTargetSchema>;

/** Flettefeltet for fornavnet. Begge skrivemåtene virker; dialogen setter inn den norske. */
export const BROADCAST_FIRST_NAME_FIELD = '{{fornavn}}';

/** Nyeste først; id-en er UUIDv7, så den er rekkefølgen utsendingene ble laget i. */
export const BROADCAST_SORT_SPEC = 'created:desc';

const ids = (max: number) => z.array(z.uuid()).max(max).default([]);

/**
 * KANALEN, VALGT PER MOTTAKER AV TVITVI (E3, QA13 47 og 253.49) og vist før sending:
 * - `inbox`: har innlogging i organisasjonen. Innboks og varsel, ikke e-post som standard.
 * - `email`: har ingen innlogging her, men en e-postadresse.
 * - `none`: har ingen av delene. Beskjeden når ikke fram, og dialogen sier det i rødt.
 */
export const BROADCAST_CHANNELS = ['inbox', 'email', 'none'] as const;
export const broadcastChannelSchema = z.enum(BROADCAST_CHANNELS);
export type BroadcastChannel = z.infer<typeof broadcastChannelSchema>;

/** «Send melding» (én person, fra personen) eller «Send beskjed» (fra lista). Samme dialog. */
export const BROADCAST_KINDS = ['broadcast', 'message'] as const;
export const broadcastKindSchema = z.enum(BROADCAST_KINDS);
export type BroadcastKind = z.infer<typeof broadcastKindSchema>;

/**
 * Hvor utsendingen hører til: en festivalårgang (`editionId`), eller et spillested (bare
 * `festivalId`, festivalen av typen `venue`).
 */
export const broadcastScopeSchema = z.object({
  festivalId: z.uuid(),
  editionId: z.uuid().nullish(),
});

/** Hvem som skal ha den. Unionen av det som er valgt; minst ett valg må være med. */
export const broadcastAudienceSchema = z
  .object({
    /** Alle i lista: årgangens personer, eller spillestedets mannskap. */
    everyone: z.boolean().default(false),
    /** De som er krysset av. */
    personIds: ids(MAX_BROADCAST_RECIPIENTS),
    /** Avdelinger i årgangen, med seksjonene under. */
    departmentIds: ids(100),
    /** Vakter: de som står på dem (ikke de som har sagt nei). */
    shiftIds: ids(500),
    /** Poster: i spillestedets mannskap, eller vaktenes post i en årgang. */
    postIds: ids(100),
    /**
     * Kategoriene (§4.1). Utelatt: alle. Filtrene (kategori, avdeling, vakt/post, dag, status)
     * snevrer inn hverandre; «alle» tar aldri med en kategori som ikke er valgt.
     */
    categories: z
      .array(broadcastRecipientCategorySchema)
      .max(BROADCAST_RECIPIENT_CATEGORIES.length)
      .optional(),
    /** Dagene (`YYYY-MM-DD` i årgangens tidssone): en vakt den dagen, eller deltakelse den dagen. */
    days: z.array(z.iso.date()).max(62).default([]),
    /** Godkjent og/eller venter. Utelatt: godkjent. */
    statuses: z.array(broadcastRecipientStatusSchema).min(1).max(2).optional(),
    /** De som er hukt av i lista. Går foran alt annet. */
    excludedPersonIds: ids(MAX_BROADCAST_RECIPIENTS),
  })
  .refine(
    (value) =>
      value.everyone ||
      value.personIds.length > 0 ||
      value.departmentIds.length > 0 ||
      value.shiftIds.length > 0 ||
      value.postIds.length > 0 ||
      value.days.length > 0 ||
      value.categories !== undefined,
    { message: 'Choose who gets it.', path: ['everyone'] },
  );

export const previewBroadcastRequestSchema = z
  .object({ scope: broadcastScopeSchema, audience: broadcastAudienceSchema })
  .meta({ id: 'PreviewBroadcastRequest' });
export type PreviewBroadcastRequest = z.infer<typeof previewBroadcastRequestSchema>;

export const createBroadcastRequestSchema = z
  .object({
    scope: broadcastScopeSchema,
    audience: broadcastAudienceSchema,
    subject: z.string().trim().min(1).max(MAX_BROADCAST_SUBJECT_LENGTH),
    body: z.string().trim().min(1).max(MAX_BROADCAST_BODY_LENGTH),
    /** «Ta med vaktene deres»: hver mottaker får sine egne vakter under meldingen (vaktplanen). */
    includeShifts: z.boolean().default(false),
    /** «Send også på e-post» til dem som har innlogging. Av som standard (253.49). */
    alsoEmail: z.boolean().default(false),
    kind: broadcastKindSchema.default('broadcast'),
    /** Blokken «Personlig lenke»: hver mottaker får lenken til der hen hører hjemme (§4.1). */
    includeLink: z.boolean().default(false),
    /** Vedlegg fra Dokumenter. */
    attachmentIds: z.array(z.uuid()).max(MAX_BROADCAST_ATTACHMENTS).default([]),
  })
  .meta({ id: 'CreateBroadcastRequest' });
export type CreateBroadcastRequest = z.infer<typeof createBroadcastRequestSchema>;

/** En mottaker slik utsendingen ser dem før den sendes. */
export const broadcastPersonSchema = z.object({
  personId: z.uuid(),
  displayName: z.string(),
  email: z.string().nullable(),
  /** Har innlogging i organisasjonen: får meldingen i Innboks. Det samme som `channel = 'inbox'`. */
  inbox: z.boolean(),
  channel: broadcastChannelSchema,
  /** Kategoriene personen har i årgangen (lista viser dem). */
  categories: z.array(broadcastRecipientCategorySchema),
  status: broadcastRecipientStatusSchema,
  /** Hvor «Personlig lenke» går for hen, eller null. */
  linkTarget: broadcastLinkTargetSchema.nullable(),
});
export type BroadcastPerson = z.infer<typeof broadcastPersonSchema>;

export const broadcastPreviewSchema = z
  .object({
    recipients: z.number().int().nonnegative(),
    withEmail: z.number().int().nonnegative(),
    withoutEmail: z.number().int().nonnegative(),
    inbox: z.number().int().nonnegative(),
    /** Hvor mange som får den på e-post (uten innlogging, med adresse), og hvor mange den ikke når. */
    emailOnly: z.number().int().nonnegative(),
    unreachable: z.number().int().nonnegative(),
    /** For mange til å sende på én gang (`MAX_BROADCAST_RECIPIENTS`). */
    tooMany: z.boolean(),
    /**
     * Dagsgrensen hos e-postleverandøren er nådd akkurat nå: det som sendes, blir liggende i køen og
     * går når grensen er tilbake.
     */
    emailHeld: z.boolean(),
    people: z.array(broadcastPersonSchema),
  })
  .meta({ id: 'BroadcastPreview' });
export type BroadcastPreview = z.infer<typeof broadcastPreviewSchema>;

/**
 * Hvordan det gikk med én mottaker. `held` er dagsgrensen hos Brevo (venter, sendes når grensen er
 * tilbake); `not_allowed` er testmiljøets mottakerliste; `no_email` er en person uten e-postadresse
 * og uten innlogging (når ikke fram); `in_inbox` er levert i Innboks, uten e-post (253.49).
 */
export const BROADCAST_DELIVERY_STATUSES = [
  'in_inbox',
  'queued',
  'held',
  'sent',
  'delivered',
  'bounced',
  'blocked',
  'failed',
  'not_allowed',
  /* 263.13: e-postdriveren sender ikke (`fake`, lokalt): «Ikke sendt (testmiljø)», som kvitteringene (260.16). */
  'not_sent',
  'no_email',
] as const;
export const broadcastDeliveryStatusSchema = z.enum(BROADCAST_DELIVERY_STATUSES);
export type BroadcastDeliveryStatus = z.infer<typeof broadcastDeliveryStatusSchema>;

export const broadcastCountsSchema = z.object({
  queued: z.number().int().nonnegative(),
  held: z.number().int().nonnegative(),
  sent: z.number().int().nonnegative(),
  failed: z.number().int().nonnegative(),
  notAllowed: z.number().int().nonnegative(),
  notSent: z.number().int().nonnegative(),
  noEmail: z.number().int().nonnegative(),
  inbox: z.number().int().nonnegative(),
});
export type BroadcastCounts = z.infer<typeof broadcastCountsSchema>;

export const broadcastSchema = z
  .object({
    id: z.uuid(),
    festivalId: z.uuid(),
    editionId: z.uuid().nullable(),
    subject: z.string(),
    body: z.string(),
    includeShifts: z.boolean(),
    senderName: z.string().nullable(),
    createdAt: wireInstant,
    kind: broadcastKindSchema,
    alsoEmail: z.boolean(),
    includeLink: z.boolean(),
    attachmentIds: z.array(z.uuid()),
    /**
     * Den felles tråden fra før 0933, da alle med innlogging sto i én lukket tråd. Nye utsendinger
     * har én samtale per mottaker (`BroadcastRecipient.threadId`), og denne er null.
     */
    threadId: z.uuid().nullable(),
    recipientCount: z.number().int().nonnegative(),
    counts: broadcastCountsSchema,
  })
  .meta({ id: 'Broadcast' });
export type Broadcast = z.infer<typeof broadcastSchema>;

export const broadcastRecipientSchema = z.object({
  personId: z.uuid(),
  displayName: z.string(),
  email: z.string().nullable(),
  inbox: z.boolean(),
  channel: broadcastChannelSchema,
  /** Fikk den også på e-post (kanalen er e-post, eller «Send også på e-post» var valgt). */
  emailed: z.boolean(),
  /** Mottakerens egen samtale med avsenderen (253.48). Bare den som har innlogging, har en. */
  threadId: z.uuid().nullable(),
  status: broadcastDeliveryStatusSchema,
  statusAt: wireInstant.nullable(),
});
export type BroadcastRecipient = z.infer<typeof broadcastRecipientSchema>;

export const broadcastDetailSchema = broadcastSchema
  .extend({ recipients: z.array(broadcastRecipientSchema) })
  .meta({ id: 'BroadcastDetail' });
export type BroadcastDetail = z.infer<typeof broadcastDetailSchema>;

export const listBroadcastsQuerySchema = z
  .object({
    festivalId: z.uuid(),
    editionId: z.uuid().optional(),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    cursor: z.string().max(MAX_CURSOR_LENGTH).optional(),
  })
  .meta({ id: 'ListBroadcastsQuery' });
export type ListBroadcastsQuery = z.infer<typeof listBroadcastsQuerySchema>;

export const broadcastListResponseSchema = page(broadcastSchema, 'BroadcastListResponse');
export type BroadcastListResponse = z.infer<typeof broadcastListResponseSchema>;

/** Valgene i dialogen: avdelingene (festival), postene (spillested) og vaktene. */
export const broadcastOptionsQuerySchema = z
  .object({ festivalId: z.uuid(), editionId: z.uuid().optional() })
  .meta({ id: 'BroadcastOptionsQuery' });
export type BroadcastOptionsQuery = z.infer<typeof broadcastOptionsQuerySchema>;

export const broadcastOptionsSchema = z
  .object({
    /** `venue` for et spillested; ellers festivalens årgang. */
    kind: z.enum(['edition', 'venue']),
    timezone: z.string(),
    departments: z.array(
      z.object({ id: z.uuid(), name: z.string(), parentId: z.uuid().nullable() }),
    ),
    posts: z.array(z.object({ id: z.uuid(), name: z.string() })),
    /** Dagene i årgangen (`YYYY-MM-DD`), for filteret Dag. Tom for et spillested. */
    days: z.array(z.iso.date()),
    shifts: z.array(
      z.object({
        id: z.uuid(),
        title: z.string(),
        startsAt: z.string(),
        endsAt: z.string(),
        /** Hvor mange som står på den (uten dem som har sagt nei). */
        assigned: z.number().int().nonnegative(),
      }),
    ),
  })
  .meta({ id: 'BroadcastOptions' });
export type BroadcastOptions = z.infer<typeof broadcastOptionsSchema>;

/**
 * FORHÅNDSVISNING AV E-POSTEN for én mottaker (E3, 45): den samme malen og de samme parametrene som
 * køen bruker, med vaktene til akkurat den personen. Ingenting lagres og ingenting sendes.
 */
export const previewBroadcastEmailRequestSchema = z
  .object({
    scope: broadcastScopeSchema,
    personId: z.uuid(),
    subject: z.string().trim().max(MAX_BROADCAST_SUBJECT_LENGTH).default(''),
    body: z.string().trim().max(MAX_BROADCAST_BODY_LENGTH).default(''),
    includeShifts: z.boolean().default(false),
    includeLink: z.boolean().default(false),
    attachmentIds: z.array(z.uuid()).max(MAX_BROADCAST_ATTACHMENTS).default([]),
  })
  .meta({ id: 'PreviewBroadcastEmailRequest' });
export type PreviewBroadcastEmailRequest = z.infer<typeof previewBroadcastEmailRequestSchema>;

export const broadcastEmailPreviewSchema = z
  .object({
    /** Adressen den ville gått til, eller null når personen ikke har en. */
    to: z.string().nullable(),
    /** Svar på e-posten går hit: avsenderens egen adresse (253.50, innkommende e-post kommer senere). */
    replyTo: z.string().nullable(),
    channel: broadcastChannelSchema,
    /** Avsenderens visningsnavn: festivalen (§4.1), ikke organisasjonen eller Tvitvi. */
    fromName: z.string(),
    linkTarget: broadcastLinkTargetSchema.nullable(),
    attachments: z.array(z.object({ id: z.uuid(), filename: z.string() })),
    subject: z.string(),
    text: z.string(),
    html: z.string(),
  })
  .meta({ id: 'BroadcastEmailPreview' });
export type BroadcastEmailPreview = z.infer<typeof broadcastEmailPreviewSchema>;

/**
 * KORRESPONDANSEN MED ÉN PERSON (46): beskjedene og meldingene hen har fått fra organisasjonen, med
 * kanalen, leveringsstatusen og samtalen. Nyeste først, med markør.
 */
export const CORRESPONDENCE_SORT_SPEC = 'created:desc';

export const correspondenceQuerySchema = z
  .object({
    personId: z.uuid(),
    limit: z.coerce.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    cursor: z.string().max(MAX_CURSOR_LENGTH).optional(),
  })
  .meta({ id: 'CorrespondenceQuery' });
export type CorrespondenceQuery = z.infer<typeof correspondenceQuerySchema>;

/**
 * `broadcast` og `message` er «Send beskjed» og «Send melding»; `automatic` er alt annet utsendingen
 * har sendt personen (bekreftelsen ved påmelding, vakt-e-postene, lenke-e-posten, invitasjonene …).
 */
export const CORRESPONDENCE_KINDS = ['broadcast', 'message', 'automatic'] as const;
export const correspondenceKindSchema = z.enum(CORRESPONDENCE_KINDS);
export type CorrespondenceKind = z.infer<typeof correspondenceKindSchema>;

export const CORRESPONDENCE_CHANNELS = ['inbox', 'email', 'sms', 'none'] as const;
export const correspondenceChannelSchema = z.enum(CORRESPONDENCE_CHANNELS);
export type CorrespondenceChannel = z.infer<typeof correspondenceChannelSchema>;

export const correspondenceItemSchema = z
  .object({
    /** Mottakerraden (beskjed) eller leveringsraden (automatisk). */
    id: z.uuid(),
    broadcastId: z.uuid().nullable(),
    kind: correspondenceKindSchema,
    /** Den automatiske malen (`signup_received`, `share_link`, `invitation` …); null for en beskjed. */
    topic: z.string().nullable(),
    subject: z.string(),
    senderName: z.string().nullable(),
    createdAt: wireInstant,
    festivalId: z.uuid().nullable(),
    editionId: z.uuid().nullable(),
    channel: correspondenceChannelSchema,
    emailed: z.boolean(),
    email: z.string().nullable(),
    threadId: z.uuid().nullable(),
    status: broadcastDeliveryStatusSchema,
    statusAt: wireInstant.nullable(),
    /** Brevo sa at e-posten er åpnet (§4.2). */
    openedAt: wireInstant.nullable(),
    /**
     * Testmiljøet: e-posten gikk til denne innboksen i stedet for til `email` (1165,
     * `MESSAGING_REDIRECT_EMAIL_TO`). Null når den gikk til mottakeren.
     */
    redirectedTo: z.string().nullable().default(null),
  })
  .meta({ id: 'CorrespondenceItem' });
export type CorrespondenceItem = z.infer<typeof correspondenceItemSchema>;

export const correspondenceListResponseSchema = page(
  correspondenceItemSchema,
  'CorrespondenceListResponse',
);
export type CorrespondenceListResponse = z.infer<typeof correspondenceListResponseSchema>;
